/**
 * Clip Tool Dialog
 *
 * Material-UI dialog for configuring clip operations.
 * Allows selection of input layer and clip boundary layer.
 *
 * **Validates: Requirements 25.1**
 */

import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import {
  setClipInputLayer,
  setClipBoundaryLayer,
  closeTool,
} from '../../store/slices/spatialAnalysisSlice';

interface ClipToolDialogProps {
  open: boolean;
  onExecute: () => void;
}

export const ClipToolDialog: React.FC<ClipToolDialogProps> = ({ open, onExecute }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { clipTool, isProcessing, error, message } = useSelector(
    (state: RootState) => state.spatialAnalysis
  );
  const layers = useSelector((state: RootState) => state.layers.layers);

  const handleClose = useCallback(() => {
    dispatch(closeTool());
  }, [dispatch]);

  const handleInputChange = useCallback(
    (e: any) => {
      dispatch(setClipInputLayer(e.target.value));
    },
    [dispatch]
  );

  const handleClipChange = useCallback(
    (e: any) => {
      dispatch(setClipBoundaryLayer(e.target.value));
    },
    [dispatch]
  );

  const polygonLayers = layers.filter(
    (l) => l.geometryType === 'Polygon' || l.geometryType === 'MultiPolygon'
  );

  const canExecute =
    clipTool.inputLayerId &&
    clipTool.clipLayerId &&
    clipTool.inputLayerId !== clipTool.clipLayerId &&
    !isProcessing;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Clip Operation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Trim features from the input layer to the boundary of the clip layer. Only portions
            inside the clip boundary are kept.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="clip-input-label">Input Layer</InputLabel>
            <Select
              labelId="clip-input-label"
              value={clipTool.inputLayerId || ''}
              onChange={handleInputChange}
              label="Input Layer"
            >
              {layers.map((layer) => (
                <MenuItem key={layer.id} value={layer.id}>
                  {layer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="clip-boundary-label">Clip Boundary Layer</InputLabel>
            <Select
              labelId="clip-boundary-label"
              value={clipTool.clipLayerId || ''}
              onChange={handleClipChange}
              label="Clip Boundary Layer"
            >
              {polygonLayers
                .filter((l) => l.id !== clipTool.inputLayerId)
                .map((layer) => (
                  <MenuItem key={layer.id} value={layer.id}>
                    {layer.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {error && <Alert severity="error">{error}</Alert>}
          {message && !error && <Alert severity="success">{message}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={!canExecute}
          startIcon={isProcessing ? <CircularProgress size={16} /> : undefined}
        >
          {isProcessing ? 'Processing...' : 'Clip Layer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClipToolDialog;
